import { z } from 'zod';
import { createEmptyPlayerLearningModel, PLAYER_MODEL_VERSION, type PlayerLearningModel } from '../bot/learning/types';
import type { CardId } from '../engine/types';

const DB_NAME = 'towerarena.com.learning';
const STORE_NAME = 'models';
const MODEL_KEY = 'local-player-style';

const styleSchema = z.object({
  aggression: z.number().int().min(0).max(1_000),
  cycle: z.number().int().min(0).max(1_000),
  beatdown: z.number().int().min(0).max(1_000),
  airPressure: z.number().int().min(0).max(1_000),
  swarm: z.number().int().min(0).max(1_000),
  spellControl: z.number().int().min(0).max(1_000),
  defense: z.number().int().min(0).max(1_000),
  lanePreference: z.number().int().min(-1_000).max(1_000),
});
const resultSchema = z.object({
  winner: z.enum(['player', 'bot', 'draw']),
  reason: z.enum(['king-destroyed', 'time', 'tiebreak']),
  crowns: z.object({ player: z.number().int().nonnegative(), bot: z.number().int().nonnegative() }),
  tick: z.number().int().nonnegative(),
});
const featuresSchema = z.object({
  matchId: z.string().min(1),
  style: styleSchema,
  plays: z.number().int().nonnegative(),
  averageCostMilli: z.number().int().nonnegative(),
  playerDeck: z.array(z.string().min(1)).length(8),
  botDeck: z.array(z.string().min(1)).length(8),
  result: resultSchema,
});
const outcomeSchema = z.object({ wins: z.number().int().nonnegative(), losses: z.number().int().nonnegative(), draws: z.number().int().nonnegative() });
const modelSchema = z.object({
  schemaVersion: z.literal(1),
  modelVersion: z.literal(PLAYER_MODEL_VERSION),
  contentVersion: z.string().min(1),
  revision: z.number().int().nonnegative(),
  matchCount: z.number().int().nonnegative(),
  confidence: z.number().int().min(0).max(1_000),
  style: styleSchema,
  processedMatchIds: z.array(z.string().min(1)).max(50),
  recentMatches: z.array(featuresSchema).max(50),
  strategyOutcomes: z.record(z.string(), outcomeSchema),
});

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Unable to open player learning database'));
    request.onblocked = () => reject(new Error('Player learning database is blocked by another tab'));
  });
}

export function parseStoredPlayerLearningModel(value: unknown, contentVersion: string): { model: PlayerLearningModel; repaired: boolean } {
  const parsed = modelSchema.safeParse(value);
  if (!parsed.success || parsed.data.contentVersion !== contentVersion) return { model: createEmptyPlayerLearningModel(contentVersion), repaired: value !== null && value !== undefined };
  return {
    repaired: false,
    model: {
      ...parsed.data,
      recentMatches: parsed.data.recentMatches.map((match) => ({
        ...match,
        playerDeck: match.playerDeck.map((cardId) => cardId as CardId),
        botDeck: match.botDeck.map((cardId) => cardId as CardId),
      })),
    },
  };
}

export async function loadPlayerLearningModel(contentVersion: string): Promise<{ model: PlayerLearningModel; repaired: boolean }> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const value = await new Promise<unknown>((resolve, reject) => {
      const request = db.transaction(STORE_NAME).objectStore(STORE_NAME).get(MODEL_KEY);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Unable to read player learning model'));
    });
    return parseStoredPlayerLearningModel(value, contentVersion);
  } finally {
    db.close();
  }
}

export async function savePlayerLearningModel(model: PlayerLearningModel): Promise<void> {
  const validated = modelSchema.parse(model);
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put(validated, MODEL_KEY);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Unable to save player learning model'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Player learning model transaction was aborted'));
    });
  } finally {
    db.close();
  }
}

export async function clearPlayerLearningModel(): Promise<void> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).delete(MODEL_KEY);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Unable to clear player learning model'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Player learning cleanup was aborted'));
    });
  } finally {
    db.close();
  }
}
